// apps/web/src/components/analytics/decide/PatchThisWeekPanel.tsx
//
// P8a Decide tab — top-10 critical vulnerabilities last 7 days, with
// KEV/exploited/high breakdown line. Each row links to
// /vulnerabilities/<cve>. Spec §4.2.

import { Link } from 'react-router-dom'
import { useAnalyticsDecidePatchThisWeek } from '@/hooks/useAnalyticsDecidePatchThisWeek'

const REASON_LABEL: Record<'kev' | 'exploited' | 'high', string> = {
  kev: 'KEV',
  exploited: 'currently exploited',
  high: 'high',
}

export function PatchThisWeekPanel() {
  const { data, isLoading } = useAnalyticsDecidePatchThisWeek()

  if (isLoading) {
    return (
      <div
        data-testid="patch-this-week-loading"
        className="h-48 animate-pulse rounded-md bg-muted"
      />
    )
  }
  if (!data || data.vulnerabilities.length === 0) {
    return (
      <section className="rounded-md border border-border p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Patch this week
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          No critical patches in the last 7 days.
        </p>
      </section>
    )
  }

  // Breakdown comes from the API aggregate over the full window — sums
  // to data.criticalCount. Previously this was derived from the visible
  // 10 rows, which produced a breakdown line that never matched the
  // headline (P8a walkthrough fix).
  const breakdown = data.breakdown ?? { kev: 0, exploited: 0, high: 0 }

  return (
    <section className="rounded-md border border-border p-4">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Patch this week
        </h2>
        <span
          data-testid="patch-this-week-headline"
          className="text-2xl font-semibold"
        >
          {data.criticalCount}
        </span>
      </header>
      <p className="mt-1 text-xs text-muted-foreground">
        Became critical-worthy in the last 7 days
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {breakdown.kev} KEV · {breakdown.exploited} exploited · {breakdown.high} high
      </p>
      <ol className="mt-3 divide-y divide-border">
        {data.vulnerabilities.map((v) => (
          <li key={v.cveId} className="py-2">
            <Link
              to={`/vulnerabilities/${v.cveId}`}
              data-testid={`patch-row-${v.cveId}`}
              className="flex items-center justify-between gap-3 text-sm hover:underline"
            >
              <span className="font-mono">{v.cveId}</span>
              <span className="text-muted-foreground">
                {REASON_LABEL[v.criticalReason]}
                {v.cvssScore !== null
                  ? ` · CVSS ${v.cvssScore.toFixed(1)}`
                  : ''}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  )
}
